/**
 * Child Store
 * Manages selected child and children list state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChildProfileWithConsent, LearningProgress, ParentSettings, HabitWithStatus, HabitStreakData, DailyChallenge } from '@kids-lms/types';
import { childService, habitService, challengeService } from '../services';

interface ChildState {
  children: ChildProfileWithConsent[];
  selectedChild: ChildProfileWithConsent | null;
  selectedChildProgress: LearningProgress[];
  selectedChildSettings: ParentSettings | null;
  isLoading: boolean;
  error: string | null;

  // Habits & challenges
  habits: HabitWithStatus[];
  streakData: HabitStreakData | null;
  dailyChallenge: DailyChallenge | null;
  showCelebration: boolean;

  // Actions
  setChildren: (children: ChildProfileWithConsent[]) => void;
  setSelectedChild: (child: ChildProfileWithConsent | null) => void;
  selectChildById: (childId: string) => void;
  setProgress: (progress: LearningProgress[]) => void;
  setSettings: (settings: ParentSettings | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Async actions
  fetchChildren: () => Promise<void>;
  fetchChildDetails: (childId: string) => Promise<void>;
  addChild: (data: Parameters<typeof childService.createChild>[0]) => Promise<ChildProfileWithConsent>;
  updateChild: (childId: string, data: Parameters<typeof childService.updateChild>[1]) => Promise<void>;
  removeChild: (childId: string) => Promise<void>;
  updateChildSettings: (childId: string, data: Parameters<typeof childService.updateSettings>[1]) => Promise<void>;

  // Habits & challenges actions
  fetchHabits: (childId: string) => Promise<void>;
  toggleHabit: (childId: string, habitId: string, completed: boolean) => Promise<void>;
  fetchDailyChallenge: (childId: string) => Promise<void>;
  setShowCelebration: (show: boolean) => void;

  // Clear state
  clearSelectedChild: () => void;
  clearAll: () => void;
}

export const useChildStore = create<ChildState>()(
  persist(
    (set, get) => ({
      children: [],
      selectedChild: null,
      selectedChildProgress: [],
      selectedChildSettings: null,
      isLoading: false,
      error: null,
      habits: [],
      streakData: null,
      dailyChallenge: null,
      showCelebration: false,

      setChildren: (children) => set({ children }),

      setSelectedChild: (child) => set({ selectedChild: child }),

      selectChildById: (childId) => {
        const child = get().children.find((c) => c.id === childId) || null;
        set({ selectedChild: child });
      },

      setProgress: (progress) => set({ selectedChildProgress: progress }),

      setSettings: (settings) => set({ selectedChildSettings: settings }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      fetchChildren: async () => {
        set({ isLoading: true, error: null });
        try {
          const children = await childService.getChildren();
          set({ children, isLoading: false });

          // Auto-select first child if none selected
          const { selectedChild } = get();
          if (!selectedChild && children.length > 0) {
            set({ selectedChild: children[0] });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch children',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchChildDetails: async (childId) => {
        set({ isLoading: true, error: null });
        try {
          const [child, progress, settings] = await Promise.all([
            childService.getChild(childId),
            childService.getProgress(childId),
            childService.getSettings(childId),
          ]);
          set({
            selectedChild: child,
            selectedChildProgress: progress,
            selectedChildSettings: settings,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch child details',
            isLoading: false,
          });
          throw error;
        }
      },

      addChild: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const newChild = await childService.createChild(data);
          set((state) => ({
            children: [...state.children, newChild],
            isLoading: false,
          }));
          return newChild;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to add child',
            isLoading: false,
          });
          throw error;
        }
      },

      updateChild: async (childId, data) => {
        set({ isLoading: true, error: null });
        try {
          const updatedChild = await childService.updateChild(childId, data);
          set((state) => ({
            children: state.children.map((c) => (c.id === childId ? updatedChild : c)),
            selectedChild: state.selectedChild?.id === childId ? updatedChild : state.selectedChild,
            isLoading: false,
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update child',
            isLoading: false,
          });
          throw error;
        }
      },

      removeChild: async (childId) => {
        set({ isLoading: true, error: null });
        try {
          await childService.deleteChild(childId);
          set((state) => ({
            children: state.children.filter((c) => c.id !== childId),
            selectedChild: state.selectedChild?.id === childId ? null : state.selectedChild,
            isLoading: false,
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to remove child',
            isLoading: false,
          });
          throw error;
        }
      },

      updateChildSettings: async (childId, data) => {
        set({ isLoading: true, error: null });
        try {
          const settings = await childService.updateSettings(childId, data);
          set({
            selectedChildSettings: settings,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update settings',
            isLoading: false,
          });
          throw error;
        }
      },

      // ---- Habits & Challenges ----

      fetchHabits: async (childId) => {
        try {
          const [habits, streakData] = await Promise.all([
            habitService.getHabits(childId),
            habitService.getStreaks(childId),
          ]);
          set({ habits, streakData });
        } catch (error) {
          console.error('Failed to fetch habits:', error);
        }
      },

      toggleHabit: async (childId, habitId, completed) => {
        // Optimistic update
        set((state) => ({
          habits: state.habits.map((h) =>
            h.id === habitId ? { ...h, completedToday: completed } : h
          ),
        }));

        try {
          const result = await habitService.checkIn(childId, habitId, completed);
          set((state) => ({
            streakData: state.streakData
              ? {
                  ...state.streakData,
                  habitPoints: result.habitPoints,
                  habitStreak: result.habitStreak,
                  todayCompleted: state.habits.filter((h) => h.completedToday).length,
                }
              : state.streakData,
          }));

          // Show celebration if all done
          if (result.allDoneBonus) {
            set({ showCelebration: true });
          }
        } catch (error) {
          // Revert optimistic update
          set((state) => ({
            habits: state.habits.map((h) =>
              h.id === habitId ? { ...h, completedToday: !completed } : h
            ),
          }));
          console.error('Failed to toggle habit:', error);
        }
      },

      fetchDailyChallenge: async (childId) => {
        try {
          const challenge = await challengeService.getDailyChallenge(childId);
          set({ dailyChallenge: challenge });
        } catch (error) {
          console.error('Failed to fetch daily challenge:', error);
        }
      },

      setShowCelebration: (show) => set({ showCelebration: show }),

      clearSelectedChild: () =>
        set({
          selectedChild: null,
          selectedChildProgress: [],
          selectedChildSettings: null,
          habits: [],
          streakData: null,
          dailyChallenge: null,
        }),

      clearAll: () =>
        set({
          children: [],
          selectedChild: null,
          selectedChildProgress: [],
          selectedChildSettings: null,
          habits: [],
          streakData: null,
          dailyChallenge: null,
          error: null,
        }),
    }),
    {
      name: 'kids-lms-child',
      partialize: (state) => ({
        selectedChild: state.selectedChild,
      }),
    }
  )
);

export default useChildStore;
